'use es6';

import tinymce from 'tinymce';
import { HS_TABLE_V2 } from '../constants/plugins';
import { TABLE_SETTINGS, hstableConfig } from '../constants/hstable2';
import { getPluginSetting } from '../utils';
import { BOGUS_BR } from '../utils/dom';
import { wrapTableInResponsiveContainer, extractTableFromResponsiveContainer, isTableNode } from '../utils/table';
import { getTableResizeHandler, handleTableDimensionChange, handleTableAlignmentChange, handleTableDimensionTypeChange, handleTableMarginChange } from './handlers';
import { setTableStyles, trackEditTableStyle } from './util';
import { addControls } from './controls';
import { addTransforms } from './transforms';
const getSelectedCellsOrTableNode = target => {
  if (isTableNode(target)) return [target];
  if (Array.isArray(target) && (target[0].nodeName === 'TD' || target[0].nodeName === 'TH')) {
    return target;
  }
  return [];
};
const addEventListeners = editor => {
  editor.on('ObjectResized', getTableResizeHandler(editor));
  if (!editor.getParam('useTableContextualToolbar')) return;
  editor.on('hsFillColorChange', ({
    target,
    color
  }) => {
    if (isTableNode(target)) return;
    const tableNodes = getSelectedCellsOrTableNode(target);
    if (!tableNodes.length) return;
    setTableStyles(editor, tableNodes, {
      'background-color': color.color
    });
  });
  editor.on('hsBorderColorChange', ({
    target,
    color
  }) => {
    const tableNodes = getSelectedCellsOrTableNode(target);
    if (!tableNodes.length) return;
    setTableStyles(editor, tableNodes, {
      'border-color': color.color
    });
  });
  editor.on('hsVerticalAlignmentChange', ({
    target,
    alignment
  }) => {
    const tableNodes = getSelectedCellsOrTableNode(target);
    if (!tableNodes.length) return;
    setTableStyles(editor, tableNodes, {
      'vertical-align': alignment
    });
  });
  editor.on('hsBorderWidthChange', ({
    target,
    width,
    borderStyle
  }) => {
    const tableNodes = getSelectedCellsOrTableNode(target);
    if (!tableNodes.length) return;
    const styles = {
      'border-width': `${width}px`
    };
    if (width === 0) {
      styles['border-style'] = 'none';
    } else if (!borderStyle || borderStyle === 'none') {
      styles['border-style'] = 'solid';
    }
    setTableStyles(editor, tableNodes, styles);
  });
  editor.on('hsBorderStyleChange', ({
    target,
    borderStyle
  }) => {
    const tableNodes = getSelectedCellsOrTableNode(target);
    if (!tableNodes.length) return;
    setTableStyles(editor, tableNodes, {
      'border-style': borderStyle
    });
  });
  editor.on('hsAlignmentChange', ({
    target,
    alignment,
    prevAlignment
  }) => {
    if (!isTableNode(target)) return;
    handleTableAlignmentChange(editor, target, alignment, prevAlignment);
  });
  editor.on('hsMarginChange', ({
    target,
    margin
  }) => {
    if (!isTableNode(target)) return;
    handleTableMarginChange(editor, target, margin);
  });
  editor.on('hsDimensionChange', ({
    target,
    dimensionType,
    dimensions,
    contextualNodeAttributes
  }) => {
    const tableNodes = getSelectedCellsOrTableNode(target);
    if (!tableNodes.length) return;
    handleTableDimensionChange(editor, tableNodes, dimensionType, dimensions, contextualNodeAttributes);
  });
  editor.on('hsTableDimensionTypeChange', ({
    target,
    dimensionType,
    tablePixelWidth
  }) => {
    handleTableDimensionTypeChange(editor, target, dimensionType, tablePixelWidth);
  });
  editor.on('hsCellPaddingChange', ({
    target,
    cellPadding
  }) => {
    const tableCells = Array.from(target.querySelectorAll('td')).filter(
    // Ignore nested table cells
    cellNode => editor.dom.getParent(cellNode, 'table') === target);
    setTableStyles(editor, tableCells, {
      padding: `${cellPadding}px`
    });
  });
  editor.on('hsTableCaptionChange', ({
    target,
    hasCaption
  }) => {
    let captionElm = editor.dom.select('caption', target)[0];
    editor.undoManager.transact(() => {
      if (!hasCaption && captionElm) {
        editor.dom.remove(captionElm);
      } else if (hasCaption && !captionElm) {
        captionElm = editor.dom.create('caption');
        const isIE = tinymce.Env.browser && tinymce.Env.browser.isIE() || tinymce.Env.ie;
        captionElm.innerHTML = isIE ? '\u00a0' : BOGUS_BR;
        target.insertBefore(captionElm, target.firstChild);
      }
    });
  });
  editor.on('hsTableResponsiveChange', ({
    target,
    isResponsive
  }) => {
    if (isResponsive) {
      wrapTableInResponsiveContainer(editor, target);
    } else {
      extractTableFromResponsiveContainer(editor, target);
    }
  });
  editor.on('hsTableCustomStyleChange', ({
    target,
    tableStyle
  }) => {
    editor.undoManager.transact(() => {
      editor.dom.setAttribs(target, {
        style: tableStyle
      });
    });
    trackEditTableStyle(editor, 'tableStyle');
  });
};
function tablePlugin(editor) {
  addControls(editor);
  addEventListeners(editor);
  addTransforms(editor);
  const {
    [hstableConfig.DEFAULT_CELL_STYLES]: defaultCellStyles
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  if (defaultCellStyles) {
    editor.on('newcell', evt => {
      const cell = evt.node;
      editor.dom.setStyles(cell, defaultCellStyles);
      const rootBlock = editor.getParam('forced_root_block');
      if (rootBlock) {
        // Replace the <br> child with the root block
        cell.replaceChild(editor.dom.create(rootBlock, {}, BOGUS_BR), cell.firstChild);
      }
    });
  }
  if (editor.settings.inline) {
    editor.on('init', () => {
      const resizeContainer = document.querySelector("div[style*='position: static; height: 0px; width: 0px; padding: 0px; margin: 0px; border: 0px;']");
      if (resizeContainer) {
        editor.dom.setAttrib(resizeContainer, 'tabIndex', -1);
        editor.dom.addClass(resizeContainer, 'mce-table-resize-container');
      }
    });
  }
}
tinymce.PluginManager.add(HS_TABLE_V2, tablePlugin);